import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { EVENTS, CONTACT } from "../data/site";
import { Reveal, Label, BookButton } from "../components/ui-kit";

export default function Events() {
  const { upcoming, past } = EVENTS;
  return (
    <>
      <section className="pt-40 pb-16 bg-black" data-testid="events-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Label>Events</Label>
          <h1 className="font-display uppercase text-white text-5xl md:text-8xl leading-[0.9] mt-6 max-w-4xl">More than a gym.</h1>
          <p className="mt-6 text-lg text-lightgrey max-w-2xl">Races, challenges, socials and community meetups — the moments that turn a workout into a family. Here's where we've been, and where we're headed next.</p>
        </div>
      </section>

      <section className="bg-black pb-8" data-testid="upcoming-events">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>Upcoming</Label></Reveal>
          {upcoming.length === 0 ? (
            <Reveal delay={0.1}>
              <div className="mt-8 rounded-3xl border border-white/10 bg-charcoal p-10 md:p-14 text-center">
                <Calendar size={40} className="text-gold mx-auto" />
                <h2 className="font-display text-3xl md:text-4xl text-white uppercase mt-5">Nothing on the calendar<br />right now</h2>
                <p className="mt-4 text-lightgrey max-w-md mx-auto">We're busy planning the next one. Follow us on social to be the first to know when the next event drops.</p>
                <div className="mt-8 flex justify-center gap-3">
                  <a href={CONTACT.instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-white hover:border-gold hover:text-gold transition" data-testid="events-ig"><Instagram size={18} /> Instagram</a>
                  <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-white hover:border-gold hover:text-gold transition" data-testid="events-fb"><Facebook size={18} /> Facebook</a>
                </div>
              </div>
            </Reveal>
          ) : (
            <div className="grid md:grid-cols-2 gap-6 mt-8">
              {upcoming.map((e, i) => <EventCard key={i} e={e} i={i} />)}
            </div>
          )}
        </div>
      </section>

      <section className="bg-black py-20" data-testid="past-events">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><Label>Highlights</Label></Reveal>
          <Reveal delay={0.1}><h2 className="font-display uppercase text-white text-4xl md:text-6xl mt-4 leading-none">Look back.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            {past.map((e, i) => <EventCard key={i} e={e} i={i} />)}
          </div>
        </div>
      </section>

      <section className="bg-charcoal py-20 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display uppercase text-white text-4xl md:text-5xl leading-none">Want in on the next one?</h2>
            <p className="mt-4 text-lightgrey">Join the community and you'll never miss an event. Start with a free intro.</p>
            <div className="mt-8 flex justify-center"><BookButton testid="events-book" /></div>
          </Reveal>
        </div>
      </section>
    </>
  );
}

function EventCard({ e, i }) {
  return (
    <Reveal delay={i * 0.08}>
      <a
        href={e.link}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`event-card-${i}`}
        className="group block rounded-3xl overflow-hidden border border-white/10 bg-charcoal hover:border-gold/40 transition-all duration-300 h-full"
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-transparent" />
          {e.tag && <span className="absolute top-4 left-4 text-xs font-bold uppercase tracking-widest bg-gold text-black px-3 py-1.5 rounded-full">{e.tag}</span>}
        </div>
        <div className="p-8">
          <h3 className="font-display text-3xl text-white uppercase">{e.title}</h3>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-gold">
            {e.date && <span className="inline-flex items-center gap-1.5"><Calendar size={15} /> {e.date}</span>}
            {e.location && <span className="inline-flex items-center gap-1.5"><MapPin size={15} /> {e.location}</span>}
          </div>
          <p className="mt-4 text-lightgrey">{e.blurb}</p>
          {e.dateISO && <Countdown target={e.dateISO} />}
          {e.gallery && (
            <div className="mt-5 grid grid-cols-3 gap-2" data-testid={`event-gallery-${i}`}>
              {e.gallery.map((g, k) => (
                <div key={k} className="aspect-square rounded-xl overflow-hidden border border-white/10">
                  <img src={g} alt={`${e.title} photo ${k + 1}`} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                </div>
              ))}
            </div>
          )}
          <span className="mt-5 inline-flex items-center gap-2 text-white font-bold group-hover:text-gold transition">{e.ctaLabel || "View on Facebook"} <ArrowUpRight size={18} /></span>
        </div>
      </a>
    </Reveal>
  );
}

function Countdown({ target }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = new Date(target).getTime() - now;
  if (diff <= 0) {
    return <p className="mt-6 font-display text-2xl text-gold uppercase" data-testid="event-countdown">Race day is here!</p>;
  }
  const units = [
    { label: "Days", value: Math.floor(diff / 86400000) },
    { label: "Hours", value: Math.floor(diff / 3600000) % 24 },
    { label: "Mins", value: Math.floor(diff / 60000) % 60 },
    { label: "Secs", value: Math.floor(diff / 1000) % 60 },
  ];
  return (
    <div className="mt-6 rounded-2xl border border-gold/25 bg-gold/5 p-4" data-testid="event-countdown">
      <p className="text-xs font-bold uppercase tracking-widest text-gold/80 text-center">Countdown to race day</p>
      <div className="mt-3 grid grid-cols-4 gap-2 text-center">
        {units.map((u) => (
          <div key={u.label} className="rounded-xl border border-white/10 bg-black/40 py-3">
            <span className="font-display text-3xl md:text-4xl text-gold leading-none">{String(u.value).padStart(2, "0")}</span>
            <span className="block mt-1 text-[10px] font-bold uppercase tracking-widest text-lightgrey/70">{u.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
